import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Motion";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { name: "", email: "", business: "", budget: "", message: "", website: "" };

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-cyan/70 focus:bg-white/[0.07]";

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/leads", form);
      setStatus("sent");
      toast.success("Quote request sent — we'll be in touch within one business day.");
    } catch (err) {
      setStatus("idle");
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="pt-40 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Overline>Contact</Overline>
        <MaskedLines
          as="h1"
          className="mt-8 font-sora font-extrabold tracking-tighter leading-[1.0] text-4xl sm:text-6xl lg:text-7xl"
          lines={["Let's talk about", "your project."]}
        />

        <div className="mt-20 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <p className="text-white/60 leading-relaxed max-w-md">
              Fill in the form and we'll come back within one business day with honest
              advice and a clear, no-pressure quote. Prefer email? That works too.
            </p>
            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-brand-cyan mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40">Email</p>
                  <a href="mailto:hello@zilleneal.com" data-testid="contact-email-link" className="text-white/80 hover:text-brand-cyan transition-colors duration-300">
                    hello@zilleneal.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-brand-cyan mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40">Studio</p>
                  <p className="text-white/80">Alberta, Canada — working worldwide</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-brand-cyan mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40">Response time</p>
                  <p className="text-white/80">Within one business day</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            {status === "sent" ? (
              <div
                className="rounded-3xl border border-brand-cyan/30 bg-surface p-12 md:p-16 text-center"
                data-testid="contact-success-panel"
              >
                <CheckCircle2 className="w-14 h-14 text-brand-cyan mx-auto" />
                <h2 className="font-sora font-bold text-3xl tracking-tight mt-8">Message received.</h2>
                <p className="text-white/60 mt-4 max-w-sm mx-auto">
                  Thanks for reaching out — your quote request is in our inbox and we'll
                  reply within one business day.
                </p>
                <button
                  onClick={() => { setForm(EMPTY); setStatus("idle"); }}
                  data-testid="contact-send-another"
                  className="mt-10 text-sm font-semibold text-brand-cyan hover:text-white transition-colors duration-300"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="rounded-3xl border border-white/10 bg-surface p-8 md:p-12"
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2.5">Your name *</label>
                    <input id="name" required value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} data-testid="contact-input-name" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2.5">Email *</label>
                    <input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@business.com" className={inputCls} data-testid="contact-input-email" />
                  </div>
                  <div>
                    <label htmlFor="business" className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2.5">Business name</label>
                    <input id="business" value={form.business} onChange={set("business")} placeholder="Jane's Café" className={inputCls} data-testid="contact-input-business" />
                  </div>
                  <div>
                    <label htmlFor="budget" className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2.5">Budget range</label>
                    <select id="budget" value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none`} data-testid="contact-input-budget">
                      <option value="" className="bg-surface">Select a range</option>
                      <option value="Under $2,000" className="bg-surface">Under $2,000</option>
                      <option value="$2,000 – $5,000" className="bg-surface">$2,000 – $5,000</option>
                      <option value="$5,000 – $10,000" className="bg-surface">$5,000 – $10,000</option>
                      <option value="$10,000+" className="bg-surface">$10,000+</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="message" className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2.5">Tell us about your project *</label>
                  <textarea id="message" required rows={5} value={form.message} onChange={set("message")} placeholder="What are you building, and what does success look like?" className={`${inputCls} resize-none`} data-testid="contact-input-message" />
                </div>

                {/* Honeypot — invisible to humans */}
                <div aria-hidden="true" className="absolute -left-[9999px] -top-[9999px]">
                  <label htmlFor="website">Website</label>
                  <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                </div>

                {error && (
                  <p className="mt-6 text-sm text-red-400" data-testid="contact-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="contact-submit-button"
                  className="mt-10 group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-9 py-4 font-semibold transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                    </>
                  ) : (
                    "Get my free quote"
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default Contact;
